/**
 * Strict parsing of untrusted op arguments (they come from the client). Every helper throws
 * RuleError('bad_args', { field, problem }) - an op never sees a value of the wrong type, and unknown fields are
 * rejected so a typo in the client cannot silently mean "default".
 */
import { RuleError } from './errors';

function bad(field: string, problem: string): never {
  throw new RuleError('bad_args', { field, problem });
}

/**
 * The arguments object with exactly the allowed fields (a subset of them may be missing; the field helpers decide
 * which are required). `undefined` and `null` count as `{}`.
 */
export function argsObject(raw: unknown, allowed: readonly string[]): Record<string, unknown> {
  if (raw === undefined || raw === null) return {};
  if (typeof raw !== 'object' || Array.isArray(raw)) bad('', 'arguments must be an object');
  const proto = Object.getPrototypeOf(raw);
  if (proto !== Object.prototype && proto !== null) bad('', 'arguments must be a plain object');
  for (const k of Object.keys(raw as object)) if (!allowed.includes(k)) bad(k, 'unknown field');
  return raw as Record<string, unknown>;
}

/** Arguments of an op that takes none: `undefined`, `null` or `{}`. */
export function noArgs(raw: unknown): Record<string, never> {
  argsObject(raw, []);
  return {};
}

export interface IntArgOptions {
  min?: number;
  max?: number;
}

/** A required safe integer in [min, max]. */
export function intArg(o: Record<string, unknown>, field: string, opts: IntArgOptions = {}): number {
  const v = o[field];
  if (v === undefined) bad(field, 'required');
  if (typeof v !== 'number' || !Number.isSafeInteger(v)) bad(field, 'must be an integer');
  if (opts.min !== undefined && v < opts.min) bad(field, `must be >= ${opts.min}`);
  if (opts.max !== undefined && v > opts.max) bad(field, `must be <= ${opts.max}`);
  return v;
}

/** An optional safe integer in [min, max] (undefined when absent). */
export function optionalIntArg(o: Record<string, unknown>, field: string, opts: IntArgOptions = {}): number | undefined {
  return o[field] === undefined ? undefined : intArg(o, field, opts);
}

export interface StringArgOptions<T extends string> {
  /** The only accepted values. */
  oneOf?: readonly T[];
  maxLength?: number;
  pattern?: RegExp;
}

/** A required string (optionally one of a set of values, bounded in length or matching a pattern). */
export function stringArg<T extends string = string>(o: Record<string, unknown>, field: string, opts: StringArgOptions<T> = {}): T {
  const v = o[field];
  if (v === undefined) bad(field, 'required');
  if (typeof v !== 'string') bad(field, 'must be a string');
  if (opts.oneOf && !opts.oneOf.includes(v as T)) bad(field, `must be one of ${opts.oneOf.join(', ')}`);
  if (opts.maxLength !== undefined && v.length > opts.maxLength) bad(field, `must be at most ${opts.maxLength} characters`);
  if (opts.pattern && !opts.pattern.test(v)) bad(field, 'has an invalid format');
  return v as T;
}

/** An optional string (undefined when absent). */
export function optionalStringArg<T extends string = string>(o: Record<string, unknown>, field: string, opts: StringArgOptions<T> = {}): T | undefined {
  return o[field] === undefined ? undefined : stringArg(o, field, opts);
}

/** A required boolean. */
export function boolArg(o: Record<string, unknown>, field: string): boolean {
  const v = o[field];
  if (v === undefined) bad(field, 'required');
  if (typeof v !== 'boolean') bad(field, 'must be a boolean');
  return v;
}

/**
 * `id` when it is an own key of the DEF table (not an inherited name like "constructor"), otherwise
 * RuleError('unknown_item'). Tables live in ctx.defs, so call it from run(), after parseArgs checked the type.
 */
export function knownKey(table: object, id: string, field = 'id'): string {
  if (!Object.prototype.hasOwnProperty.call(table, id)) throw new RuleError('unknown_item', { field, id });
  return id;
}
